import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';
import SearchableSelect from '../../components/common/SearchableSelect';
import { standaloneActService } from '../../services/standaloneActService';

export const ACT_TYPES = {
  PRELUNGIRE: 'Prelungire',
  MODIFICARE_SERVICII: 'ModificareServicii',
};

export const ACT_TYPE_LABELS = {
  [ACT_TYPES.PRELUNGIRE]: 'Prelungire contract',
  [ACT_TYPES.MODIFICARE_SERVICII]: 'Modificare servicii și preț',
};

const BENEFICIARY_GROUP = 'Beneficiar';

const today = () => new Date().toISOString().split('T')[0];

const emptyServiceLine = () => ({ name: '', description: '', quantity: 1, unit: '', unitPrice: 0 });

const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * Formular pentru actele adiționale standalone (fără contract în aplicație).
 * Câmpurile sunt variabilele template-ului, precompletate cu datele firmei și ale clientului ales.
 */
const StandaloneActFormModal = ({ isOpen, onClose, act, clients = [], catalogServices = [], onSaved }) => {
  const isEdit = !!act;

  const [actType, setActType] = useState(ACT_TYPES.PRELUNGIRE);
  const [clientId, setClientId] = useState('');
  const [number, setNumber] = useState('1');
  const [date, setDate] = useState(today());
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [serviceLines, setServiceLines] = useState([]);
  const [accessSessionsCount, setAccessSessionsCount] = useState(1);
  const [loadingForm, setLoadingForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const isServicesAct = actType === ACT_TYPES.MODIFICARE_SERVICII;

  // Încarcă variabilele template-ului; valorile existente au prioritate față de cele implicite
  const loadForm = async (type, selectedClientId, keepValues, overwriteBeneficiary = false) => {
    try {
      setLoadingForm(true);
      const form = await standaloneActService.getForm(type, selectedClientId || null);
      setFields(form.variables);
      setValues(() => {
        const next = {};
        form.variables.forEach((field) => {
          const kept = keepValues[field.key];
          const useDefault = kept === undefined || kept === ''
            || (overwriteBeneficiary && field.group === BENEFICIARY_GROUP);
          next[field.key] = useDefault ? field.value : kept;
        });
        return next;
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Eroare la încărcarea formularului');
    } finally {
      setLoadingForm(false);
    }
  };

  // Inițializare la deschidere
  useEffect(() => {
    if (!isOpen) return;

    if (act) {
      setActType(act.actType);
      setClientId(act.clientId || '');
      setNumber(act.number);
      setDate(act.date ? act.date.split('T')[0] : today());
      setServiceLines(act.services?.length ? act.services : []);
      setAccessSessionsCount(act.accessSessionsCount || 1);
      loadForm(act.actType, act.clientId, act.variables || {});
    } else {
      setActType(ACT_TYPES.PRELUNGIRE);
      setClientId('');
      setNumber('1');
      setDate(today());
      setServiceLines([emptyServiceLine()]);
      setAccessSessionsCount(1);
      loadForm(ACT_TYPES.PRELUNGIRE, null, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, act]);

  const handleActTypeChange = (type) => {
    setActType(type);
    if (type === ACT_TYPES.MODIFICARE_SERVICII && serviceLines.length === 0) {
      setServiceLines([emptyServiceLine()]);
    }
    loadForm(type, clientId, values);
  };

  const handleClientChange = (value) => {
    setClientId(value || '');
    // Datele beneficiarului se reiau din clientul ales; restul câmpurilor rămân cum sunt
    loadForm(actType, value, values, true);
  };

  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const groupedFields = useMemo(() => {
    const groups = [];
    fields.forEach((field) => {
      let group = groups.find((g) => g.name === field.group);
      if (!group) {
        group = { name: field.group, fields: [] };
        groups.push(group);
      }
      group.fields.push(field);
    });
    return groups;
  }, [fields]);

  // Serviciile Anexei 1
  const updateLine = (index, field, value) => {
    setServiceLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  };

  const removeLine = (index) => setServiceLines((prev) => prev.filter((_, i) => i !== index));

  const addCatalogService = (serviceId) => {
    const service = catalogServices.find((s) => s.id.toString() === serviceId?.toString());
    if (!service) return;
    setServiceLines((prev) => [
      // Înlocuiește un rând gol, dacă există
      ...prev.filter((line) => line.name.trim() !== ''),
      {
        name: service.name,
        description: service.description || '',
        quantity: 1,
        unit: service.unit || '',
        unitPrice: service.price,
      },
    ]);
  };

  const servicesTotal = serviceLines.reduce(
    (total, line) => total + (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0),
    0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!number.trim() || !date) {
      toast.error('Numărul și data actului sunt obligatorii');
      return;
    }

    if (isServicesAct) {
      if (serviceLines.length === 0 || serviceLines.some((l) => !l.name.trim() || !(Number(l.quantity) > 0))) {
        toast.error('Fiecare serviciu din Anexa 1 trebuie să aibă denumire și cantitate mai mare decât 0');
        return;
      }
      if (!(Number(accessSessionsCount) > 0)) {
        toast.error('Numărul de sesiuni de acces trebuie să fie cel puțin 1');
        return;
      }
    }

    const payload = {
      Number: number.trim(),
      Date: date,
      ActType: actType,
      ClientId: clientId ? parseInt(clientId) : null,
      Variables: values,
      Services: isServicesAct
        ? serviceLines.map((l) => ({
            Name: l.name.trim(),
            Description: (l.description || '').trim(),
            Quantity: parseInt(l.quantity),
            Unit: (l.unit || '').trim(),
            UnitPrice: parseFloat(l.unitPrice) || 0,
          }))
        : [],
      AccessSessionsCount: isServicesAct ? parseInt(accessSessionsCount) : null,
      AutoGeneratePdf: true,
    };

    try {
      setSaving(true);
      const saved = isEdit
        ? await standaloneActService.update(act.id, payload)
        : await standaloneActService.create(payload);
      toast.success(isEdit ? 'Actul adițional a fost actualizat și PDF-ul regenerat' : 'Actul adițional a fost creat');
      onSaved(saved);
    } catch (error) {
      const data = error.response?.data;
      const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : null;
      toast.error(validationErrors || data?.message || 'Eroare la salvarea actului adițional');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? `Editează Act Adițional nr. ${act.number}` : 'Act Adițional Standalone Nou'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date generale */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tip act adițional *</label>
            <select value={actType} onChange={(e) => handleActTypeChange(e.target.value)} className={inputClass}>
              {Object.values(ACT_TYPES).map((type) => (
                <option key={type} value={type}>{ACT_TYPE_LABELS[type]}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client existent (opțional)</label>
            <SearchableSelect
              options={clients}
              value={clientId}
              onChange={handleClientChange}
              placeholder="Precompletează din client..."
              searchPlaceholder="Căutare client..."
              getOptionLabel={(c) => c.name}
              getOptionValue={(c) => c.id}
              emptyMessage="Nu s-au găsit clienți"
            />
            <p className="mt-1 text-xs text-gray-500">Doar precompletează datele beneficiarului; le poți modifica mai jos</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Număr act adițional *</label>
            <input type="text" value={number} onChange={(e) => setNumber(e.target.value)} maxLength={50} required className={inputClass} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data actului adițional *</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required className={inputClass} />
          </div>
        </div>

        {/* Variabilele template-ului, pe grupuri */}
        {loadingForm ? (
          <p className="text-sm text-gray-500">Se încarcă câmpurile template-ului...</p>
        ) : (
          groupedFields.map((group) => (
            <fieldset key={group.name} className="border border-gray-200 rounded-md p-4">
              <legend className="px-2 text-sm font-semibold text-gray-800">{group.name}</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {group.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.label !== field.key && <span className="text-gray-400 font-normal"> · {field.key}</span>}
                    </label>
                    <input
                      type={field.type === 'date' ? 'date' : 'text'}
                      value={values[field.key] ?? ''}
                      onChange={(e) => setValue(field.key, e.target.value)}
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>
            </fieldset>
          ))
        )}

        {/* Anexa 1 + sesiuni de acces */}
        {isServicesAct && (
          <fieldset className="border border-gray-200 rounded-md p-4 space-y-3">
            <legend className="px-2 text-sm font-semibold text-gray-800">Anexa 1 și sesiuni de acces</legend>

            <div className="max-w-xs">
              <label className="block text-xs font-medium text-gray-700 mb-1">Număr sesiuni de acces *</label>
              <input
                type="number"
                min="1"
                value={accessSessionsCount}
                onChange={(e) => setAccessSessionsCount(parseInt(e.target.value) || '')}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Adaugă din catalogul de servicii</label>
                <SearchableSelect
                  options={catalogServices}
                  value=""
                  onChange={addCatalogService}
                  placeholder="Alege un serviciu..."
                  searchPlaceholder="Căutare serviciu..."
                  getOptionLabel={(s) => `${s.name} (${s.price} lei/${s.unit})`}
                  getOptionValue={(s) => s.id}
                  emptyMessage="Nu s-au găsit servicii"
                />
              </div>
              <Button type="button" variant="outline" icon={Plus} onClick={() => setServiceLines((prev) => [...prev, emptyServiceLine()])}>
                Rând gol
              </Button>
            </div>

            {serviceLines.map((line, index) => (
              <div key={index} className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end border border-gray-100 rounded-md p-2">
                <div className="col-span-2 md:col-span-3">
                  <label className="block text-xs text-gray-600 mb-1">Denumire *</label>
                  <input type="text" value={line.name} onChange={(e) => updateLine(index, 'name', e.target.value)} className={inputClass} />
                </div>
                <div className="col-span-2 md:col-span-4">
                  <label className="block text-xs text-gray-600 mb-1">Descriere</label>
                  <input type="text" value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} className={inputClass} />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs text-gray-600 mb-1">Cant.</label>
                  <input type="number" min="1" value={line.quantity} onChange={(e) => updateLine(index, 'quantity', parseInt(e.target.value) || '')} className={inputClass} />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs text-gray-600 mb-1">UM</label>
                  <input type="text" value={line.unit} onChange={(e) => updateLine(index, 'unit', e.target.value)} placeholder="lună" className={inputClass} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-600 mb-1">Preț unitar (lei)</label>
                  <input type="number" step="0.01" min="0" value={line.unitPrice} onChange={(e) => updateLine(index, 'unitPrice', e.target.value)} className={inputClass} />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <button type="button" onClick={() => removeLine(index)} className="p-2 text-red-600 hover:text-red-800" title="Șterge serviciul">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            <div className="text-right text-sm">
              Total lunar: <strong>{servicesTotal.toFixed(2)} lei</strong>
            </div>
          </fieldset>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={onClose}>Anulează</Button>
          <Button type="submit" variant="primary" loading={saving} disabled={saving || loadingForm}>
            {isEdit ? 'Salvează și regenerează PDF' : 'Creează și generează PDF'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default StandaloneActFormModal;
